import { QueryCache, type Query } from '@tanstack/react-query';

/**
 * Per-query error handling for the app's QueryClients.
 *
 * v5 removes the per-`useQuery` `onError` callback, so error notifications must
 * live on a single cache-level handler. We adopt that pattern now (it is valid
 * on v4 too) and drive per-query intent through `meta`:
 *
 *   meta: { suppressErrorToast: true }   -> log only, no toast
 *   meta: { errorToast: 'some message' } -> show this exact message instead of
 *                                           the generic backend-derived one
 *   meta: { inlineError: new InlineQueryError(check) }
 *                                        -> no toast when `check` says the caller
 *                                           shows this failure in place (asked as
 *                                           the fetch fails)
 *   (no meta)                            -> generic toast via resolveGenericMessage
 *
 * This reproduces v4's option-override semantics (a per-query onError replaced
 * the global default) without scattering effects across the call sites.
 */
export interface QueryErrorHandlerConfig {
	/** How this app surfaces an error toast (showToastV2 vs. useDSToast, etc.). */
	showErrorToast: (message: string) => void;
	/**
	 * Generic message shown when a query opts into neither a custom message nor
	 * suppression. Omit (or return undefined) to never show a generic toast —
	 * used by the investor app, which had no global error toast.
	 */
	resolveGenericMessage?: (error: unknown) => string | undefined;
	/**
	 * Prevents bursts of identical query errors from producing multiple toasts.
	 * Defaults to 1000ms.
	 */
	dedupeWindowMs?: number;
}

export const createQueryErrorHandler = ({
	showErrorToast,
	resolveGenericMessage,
	dedupeWindowMs = 1000,
}: QueryErrorHandlerConfig) => {
	let lastToast: { message: string; shownAt: number } | undefined;

	const showDedupedErrorToast = (message: string): void => {
		const now = Date.now();
		if (lastToast?.message === message && now - lastToast.shownAt < dedupeWindowMs) {
			return;
		}

		lastToast = { message, shownAt: now };
		showErrorToast(message);
	};

	return (error: unknown, meta: Record<string, unknown> | undefined): void => {
		// eslint-disable-next-line no-console
		console.error(error);

		if (meta?.suppressErrorToast === true) {
			return;
		}

		const customMessage = meta?.errorToast;
		if (typeof customMessage === 'string') {
			showDedupedErrorToast(customMessage);
			return;
		}

		const genericMessage = resolveGenericMessage?.(error);
		if (genericMessage) {
			showDedupedErrorToast(genericMessage);
		}
	};
};

export const suppressMutationErrorToast = (): void => undefined;

/** Goes on a query's `meta.inlineError`: tells, as a fetch fails, whether the caller shows that failure in place. */
export class InlineQueryError {
	readonly showsInPlace: (query: Query<unknown, unknown, unknown>) => boolean;

	constructor(showsInPlace: (query: Query<unknown, unknown, unknown>) => boolean) {
		this.showsInPlace = showsInPlace;
	}
}

export const createAppQueryCache = (config: QueryErrorHandlerConfig): QueryCache => {
	const handleError = createQueryErrorHandler(config);
	return new QueryCache({
		onError: (error, query) => {
			const inline = query.meta?.inlineError;
			handleError(
				error,
				inline instanceof InlineQueryError && inline.showsInPlace(query)
					? { ...query.meta, suppressErrorToast: true }
					: query.meta
			);
		},
	});
};
